import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../../config/appConfig';
import type { AccPeriod } from './useData';

export type AccPeriodStatus = AccPeriod['status'];

export const isPeriodLocked = (item: Pick<AccPeriod, 'status'>) => item.status === 'LOCKED';

export const formatPeriodStatus = (status: AccPeriodStatus) => (status === 'OPEN' ? 'Open' : 'Locked');

const toPeriodPayload = (data: Record<string, unknown>) => ({
  year: Number(data.year),
  startDate: data.startDate,
  endDate: data.endDate,
  status: data.status ?? 'OPEN'});

export const moduleConfig = {
  module: {
    title: 'Periods',
    permissionModuleKey: 'acc_periods',
    route: {
      list: '/accounting/periods',
      new: '/accounting/periods/new',
      detail: (id: number) => `/accounting/periods/${id}`,
      edit: (id: number) => `/accounting/periods/${id}/edit`},
    api: { list: '/acc-periods', detail: (id: number) => `/acc-periods/${id}` }},
  pagination: { initialItemsPerPage: APP_CONFIG.table.defaultPageSize, itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions },
  search: { placeholder: 'Search by year…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: AccPeriod[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New period', showTitle: false, variant: 'primary' as const, href: '/accounting/periods/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccPeriod) => item.id,
  },
  emptyStates: { noData: 'Create a period.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete period',
    message: 'Delete this period?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deleteAccPeriod } = await import('../../../services/mutations');
      await deleteAccPeriod(id);
    }},
  utils: { formatDate: (d?: string) => (d ? formatDateGlobal(d) : '') },
  detail: {
    subtitle: 'Accounting period',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Period not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Period',
    getTitle: (item: AccPeriod) => `${item.year} (${formatPeriodStatus(item.status)})`},
  form: {
    new: { title: 'New period', subtitle: 'Create an accounting period for journal posting', submitText: 'Create' },
    edit: { title: 'Edit period', subtitle: 'Update period dates and status', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formSizing: 'content' as const,
    formMaxWidth: 'max-w-3xl',
    disableWhen: (item: AccPeriod) => isPeriodLocked(item),
    disabledMessage: 'This period is locked. Unlock it from the detail page before editing.',
    create: async (data: Partial<AccPeriod>) => {
      const { createAccPeriod } = await import('../../../services/mutations');
      return createAccPeriod(toPeriodPayload(data as Record<string, unknown>));
    },
    update: async (id: number, data: Partial<AccPeriod>) => {
      const { updateAccPeriod } = await import('../../../services/mutations');
      return updateAccPeriod(id, toPeriodPayload(data as Record<string, unknown>));
    }},
  fields: [
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      validation: { required: true, min: 1900, max: 2100 }},
    {
      name: 'startDate',
      label: 'Start date',
      type: 'date',
      required: true,
      validation: { required: true }},
    {
      name: 'endDate',
      label: 'End date',
      type: 'date',
      required: true,
      validation: { required: true }},
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [
        { value: 'OPEN', label: 'Open' },
        { value: 'LOCKED', label: 'Locked' },
      ],
      helperText: 'Locked periods cannot be edited or deleted. Journals cannot be added, changed, or removed for dates in a locked period.',
      defaultValue: 'OPEN',
      validation: { required: true }},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'identification',
        title: 'Period',
        description: 'Define the fiscal year and date range.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['year', 'startDate', 'endDate'],
        fieldSpans: {
          year: 'md:col-span-4',
          startDate: 'md:col-span-4',
          endDate: 'md:col-span-4'},
        collapsible: false},
      {
        id: 'status',
        title: 'Status',
        description: 'Open periods accept journal entries. Lock a period after month-end or year-end close.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['status'],
        fieldSpans: {
          status: 'md:col-span-6'},
        collapsible: false},
    ]} as FormLayout};

export const getColumns = (formatDate: (d?: string) => string): Column<AccPeriod>[] => [
  { key: 'year', header: 'Year', sortable: true },
  { key: 'startDate', header: 'Start', render: (v) => formatDate(v) },
  { key: 'endDate', header: 'End', render: (v) => formatDate(v) },
  {
    key: 'status',
    header: 'Status',
    render: (v) => (
      <Badge variant={v === 'OPEN' ? 'success' : 'warning'}>{formatPeriodStatus(v)}</Badge>
    )},
];

export const getDetailSections = (item: AccPeriod, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Year', value: String(item.year) },
      { label: 'Start', value: formatDate(item.startDate) },
      { label: 'End', value: formatDate(item.endDate) },
      { label: 'Status', value: formatPeriodStatus(item.status) },
    ]},
];

export const getTableActions = createGetTableActions<AccPeriod>(moduleConfig.module.route, {
  showEdit: (item) => !isPeriodLocked(item),
  showDelete: (item) => !isPeriodLocked(item)});

export const getToolbarIcon = getAccountingToolbarIcon;

const StatusBadges: React.FC<{ item: AccPeriod }> = ({ item }) => (
  <Badge variant={item.status === 'OPEN' ? 'success' : 'warning'}>
    {formatPeriodStatus(item.status)}
  </Badge>
);

export const getStatusBadges = (item: AccPeriod) => <StatusBadges item={item} />;

export const getInitialFormData = (): Partial<AccPeriod> => ({
  year: new Date().getFullYear(),
  startDate: '',
  endDate: '',
  status: 'OPEN'});

export const mapItemToFormData = (item: AccPeriod): Partial<AccPeriod> => ({
  year: item.year,
  startDate: item.startDate?.slice(0, 10) ?? '',
  endDate: item.endDate?.slice(0, 10) ?? '',
  status: item.status});
